import { useEffect, useState } from 'react';

import { Spinner } from '@components';
import { STORAGE_KEYS } from '@constants/storage';
import { useLazyGetLeadPaymentOrderHistoryQuery } from '@features/lead-management/queries/LeadManagementQuery';
import { buildPaymentHistoryDisplayRows } from '@features/transactions/helpers/paymentHistoryDisplayRows';
import { useLazyGetPaymentOrderHistoryQuery } from '@features/transactions/queires/TransactionsQuery';
import { getLocalStorageItem } from '@utils/storage';

import TransactionHistory from '../transaction-history/TransactionHistory';

interface PaymentHistoryProps {
  branchId: string;
  orderId: string;
  historyOrderId?: string;
  leadId?: string;
}

const PaymentHistory = ({
  branchId,
  orderId,
  historyOrderId,
  leadId
}: PaymentHistoryProps) => {
  const effectiveOrderId = historyOrderId ?? orderId;
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const [paymentHistories, setPaymentHistory] = useState<any>([]);

  const [
    getPaymentOrderHistory,
    {
      isLoading: paymentOrderHistoryLoading,
      isFetching: paymentHistoryFetching,
      isUninitialized: paymentHistoryUninitialized
    }
  ] = useLazyGetPaymentOrderHistoryQuery();

  const [
    getLeadPaymentOrderHistory,
    {
      isLoading: leadPaymentOrderHistoryLoading,
      isFetching: leadPaymentHistoryFetching,
      isUninitialized: leadPaymentHistoryUninitialized
    }
  ] = useLazyGetLeadPaymentOrderHistoryQuery();

  const handleGetPaymentOrderHistoryDetails = async () => {
    if (leadId) {
      const res = await getLeadPaymentOrderHistory({
        organizationId,
        leadId,
        branchId,
        paymentOrderId: orderId
      });
      if ('data' in res) {
        setPaymentHistory(res.data?.data || res.data);
      }
    } else {
      const res = await getPaymentOrderHistory({
        params: { organizationId, branchId, orderId: effectiveOrderId }
      });
      if ('data' in res) {
        setPaymentHistory(res.data);
      }
    }
  };

  const paymentHistoryData = paymentHistories.map(
    (paymentHistory: any, historyIndex: number) => ({
      data: buildPaymentHistoryDisplayRows(paymentHistory),
      type: paymentHistory.type as string | undefined,
      key: `${paymentHistory.paid_at || ''}-${
        paymentHistory.paid_amount || ''
      }-${historyIndex}`
    })
  );

  const isLoadingPaymentHistory = leadId
    ? leadPaymentOrderHistoryLoading ||
      leadPaymentHistoryFetching ||
      leadPaymentHistoryUninitialized
    : paymentOrderHistoryLoading ||
      paymentHistoryFetching ||
      paymentHistoryUninitialized;

  useEffect(() => {
    handleGetPaymentOrderHistoryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrderId, leadId, branchId]);

  return isLoadingPaymentHistory ? (
    <Spinner />
  ) : (
    <>
      {paymentHistoryData.length > 0 && (
        <div className="w-full rounded-xl border border-grey40 px-4">
          {paymentHistoryData.map((paymentHistoryItem: any) => (
            <TransactionHistory
              key={paymentHistoryItem.key}
              isLoading={paymentOrderHistoryLoading}
              paymentHistoryVal={paymentHistoryItem.data}
              type={paymentHistoryItem.type}
            />
          ))}
        </div>
      )}
      {paymentHistoryData.length === 0 && (
        <div className="text-center text-sm font-medium text-grey">
          No transactions to show
        </div>
      )}
    </>
  );
};

export default PaymentHistory;
